import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Search, Zap, ShieldCheck, Globe2, MapPin, QrCode, Truck, Users, Smartphone } from "lucide-react";
import api from "../api";
import { useAuth } from "../auth";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { MaskedLine, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ReferralBanner from "../components/ReferralBanner";
import { HomeBlogGrid } from "../components/HomeBlogGrid";

const FEATURED_ORDER = ["JP", "IT", "US", "FR", "TH", "TR"];
const PILLS = ["JP", "IT", "US", "FR", "TH"];
const HERO_IMG = "https://images.unsplash.com/photo-1711535767116-6bc0d7948fb9?crop=entropy&cs=srgb&fm=jpg&q=85";

function Flag({ code, size = "w-5 h-5" }) {
  return (
    <img
      src={`https://flagcdn.com/w80/${code.toLowerCase()}.png`}
      alt={code}
      loading="lazy"
      className={`${size} rounded-full object-cover border-2 border-white shadow-sm shrink-0`}
    />
  );
}

function Confetti() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute top-[10%] left-[4%] w-14 h-7 rounded-full bg-[#FFEDD5] animate-float" style={{ "--r": "12deg" }} />
      <div className="absolute top-[26%] right-[6%] w-9 h-9 rounded-full bg-[#E0F2FE] animate-float-delayed" />
      <div className="absolute bottom-[24%] left-[10%] w-7 h-7 rounded-lg bg-[#D1FAE5] animate-float" style={{ "--r": "45deg", animationDelay: "0.8s" }} />
      <div className="absolute top-[58%] right-[14%] w-10 h-5 rounded-full bg-[#F3E8FF] animate-float-delayed" style={{ "--r": "-12deg" }} />
      <div className="absolute bottom-[10%] right-[34%] w-6 h-6 rounded-md bg-[#FFEDD5] animate-float" style={{ "--r": "45deg", animationDelay: "2s" }} />
    </div>
  );
}

export default function Home() {
  usePageMeta("seo.homeTitle", "seo.homeDesc");
  const { t, regionName, fmt, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [featured, setFeatured] = useState([]);
  const [mock, setMock] = useState(false);
  const [daily, setDaily] = useState(null);
  const [trip, setTrip] = useState(null);
  const [globalDest, setGlobalDest] = useState(null);
  const [regionals, setRegionals] = useState([]);
  const [regIdx, setRegIdx] = useState(0);
  const [homeBanner, setHomeBanner] = useState(null);  // banner marketing (Admin → Marketing)

  useEffect(() => {
    api.get("/destinations?type=global").then(({ data }) => setGlobalDest((data.data || [])[0] || null)).catch(() => {});
    api.get("/destinations?type=regional").then(({ data }) => setRegionals(data.data || [])).catch(() => {});
    api.get("/marketing/public").then(({ data }) => setHomeBanner(data.data?.home_banner || null)).catch(() => {});
  }, []);

  // La card regionale ruota: Europa, Asia, Caraibi… ogni 4 secondi
  useEffect(() => {
    if (regionals.length < 2) return undefined;
    const tm = setInterval(() => setRegIdx((i) => (i + 1) % regionals.length), 4000);
    return () => clearInterval(tm);
  }, [regionals]);
  const regional = regionals.length ? regionals[regIdx % regionals.length] : null;

  useEffect(() => {
    if (!user) { setTrip(null); return; }
    api.get("/my/trip").then(({ data }) => setTrip(data.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get("/destinations?type=local").then(({ data }) => {
      const all = data.data || [];
      setMock(!!data.meta?.mock);
      const picked = FEATURED_ORDER.map((c) => all.find((d) => d.code === c)).filter(Boolean);
      setFeatured(picked.length >= 4 ? picked : all.slice(0, 6));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/highlights/daily?lang=${lang}`).then(({ data }) => setDaily(data.data)).catch(() => {});
  }, [lang]);

  const imgSrc = (u) => (u?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  const search = (e) => {
    e.preventDefault();
    navigate(`/destinations${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  return (
    <div data-testid="home-page">
      {/* FASCIA VIAGGIO (utente loggato con viaggio in corso o in arrivo) */}
      {trip && (
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto mt-6">
          <div data-testid="home-trip-strip"
            className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-3xl bg-white border border-slate-200/80 shadow-sm px-6 py-4">
            <span className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("home.yourTrip")}</span>
            <span className="font-display font-bold text-slate-900">{trip.destination || trip.package_title}</span>
            <span data-testid="home-trip-countdown" className="rounded-full bg-[#0D9488]/10 text-[#0D9488] px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest">
              {trip.in_trip ? t("acc.onTrip")
                : trip.days_left > 1 ? t("acc.departIn").replace("{n}", trip.days_left)
                : trip.days_left === 1 ? t("acc.departTomorrow") : t("acc.departToday")}
            </span>
            {trip.gb_left != null && (
              <span data-testid="home-trip-gb" className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {t("home.gbLeft").replace("{gb}", trip.gb_left)}
              </span>
            )}
            <Link data-testid="home-trip-topup" to="/topup"
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#FF6B35] text-white px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors">
              <Zap className="w-3.5 h-3.5" /> {t("home.topupNow")}
            </Link>
          </div>
        </div>
      )}
      {/* HERO */}
      <section data-testid="hero-section" className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pt-14 sm:pt-20 pb-20">
        <Confetti />
        <div className="relative grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
          <div>
            <Reveal y={16}>
              <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-6 flex items-center gap-3">
                <span className="w-8 h-1 rounded-full bg-[#FF6B35]" />
                {t("hero.eyebrow")}
              </div>
            </Reveal>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-slate-900">
              <MaskedLine delay={0.1}>{t("hero.l1")}</MaskedLine>
              <MaskedLine delay={0.22} className="text-[#FF6B35]">{t("hero.l2")}</MaskedLine>
              <MaskedLine delay={0.34} className="text-slate-500">{t("hero.l3")}</MaskedLine>
            </h1>
            <Reveal delay={0.5} className="mt-7 max-w-md">
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">{t("hero.sub")}</p>
            </Reveal>
            <Reveal delay={0.62} className="mt-8">
              <form onSubmit={search} className="flex max-w-md rounded-full border border-slate-200 bg-white shadow-md shadow-orange-100/60 overflow-hidden focus-within:border-[#FF6B35] transition-colors">
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("hero.searchPh")}
                  className="flex-1 px-6 py-4 bg-transparent outline-none text-sm"
                />
                <button data-testid="hero-search-submit" type="submit" className="px-6 bg-[#FF6B35] text-white hover:bg-[#E8551F] transition-colors">
                  <Search className="w-4 h-4" />
                </button>
              </form>
              <div className="flex flex-wrap gap-2.5 mt-5">
                {PILLS.map((c) => (
                  <Link key={c} to={`/destination/${c}`} data-testid={`hero-pill-${c}`}
                    className="flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-medium shadow-sm hover:border-[#FF6B35] hover:-translate-y-0.5 transition-all duration-200">
                    <Flag code={c} />
                    {regionName(c)}
                  </Link>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.75} className="mt-12 flex gap-10">
              {[["200+", t("hero.stat1l")], ["11", t("hero.stat2l")], ["60s", t("hero.stat3l")]].map(([v, l]) => (
                <div key={l}>
                  <div className="font-display font-extrabold text-3xl text-slate-900">{v}</div>
                  <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-1">{l}</div>
                </div>
              ))}
            </Reveal>
          </div>
          <div className="relative hidden lg:block">
            <motion.div
              data-testid="hero-esim-card"
              initial={{ opacity: 0, y: 48, rotate: 4 }}
              animate={{ opacity: 1, y: 0, rotate: 2 }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-orange-200/60"
            >
              <img src={HERO_IMG} alt="traveller" className="w-full h-[430px] object-cover" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -left-10 bottom-10 rounded-2xl bg-white shadow-xl shadow-slate-200 px-5 py-4 flex items-center gap-3 animate-float"
            >
              <span className="w-10 h-10 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center">
                <QrCode className="w-5 h-5" />
              </span>
              <div>
                <div className="font-display font-bold text-sm text-slate-900">eSIM QR</div>
                <div className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("misc.instant")}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Banner marketing modificabile da Admin → Marketing */}
      {homeBanner && (
        <section data-testid="home-marketing-banner" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto mt-6">
          <Reveal>
            <Link to={homeBanner.link || "/destinations"}
              className="group relative flex flex-wrap items-center gap-x-5 gap-y-2 overflow-hidden rounded-3xl bg-[#1E293B] px-6 sm:px-8 py-5 text-white shadow-xl shadow-slate-200">
              <div className="absolute -right-10 -top-10 w-36 h-36 rounded-full bg-[#FF6B35]/25 blur-2xl" aria-hidden="true" />
              <span className="relative font-display font-extrabold text-lg sm:text-xl tracking-tight">
                {lang === "it" ? (homeBanner.title_it || homeBanner.title_en) : (homeBanner.title_en || homeBanner.title_it)}
              </span>
              <span className="relative text-sm text-slate-300">
                {lang === "it" ? (homeBanner.text_it || homeBanner.text_en) : (homeBanner.text_en || homeBanner.text_it)}
              </span>
              {(lang === "it" ? (homeBanner.cta_it || homeBanner.cta_en) : (homeBanner.cta_en || homeBanner.cta_it)) && (
                <span data-testid="home-banner-cta" className="relative ml-auto inline-flex items-center gap-2 rounded-full bg-[#FF6B35] px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.18em] group-hover:bg-[#E8551F] transition-colors">
                  {lang === "it" ? (homeBanner.cta_it || homeBanner.cta_en) : (homeBanner.cta_en || homeBanner.cta_it)}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              )}
            </Link>
          </Reveal>
        </section>
      )}

      <ReferralBanner />

      {/* FEATURED DESTINATIONS */}
      <section data-testid="featured-destinations" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-24">
        <Reveal>
          <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("featured.eyebrow")}</div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">{t("featured.title")}</h2>
            <p className="max-w-sm text-slate-600">{t("featured.sub")}</p>
          </div>
        </Reveal>
        {mock && (
          <div data-testid="demo-catalog-banner" className="mb-8 rounded-2xl border border-amber-300/60 bg-amber-50 px-5 py-3 text-sm text-slate-600">
            <span className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF6B35] mr-3">{t("misc.demo")}</span>
            {t("misc.demoSub")}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {globalDest && (
            <Reveal key="global">
              <Link to="/destination/global" data-testid="destination-card-global"
                className="group block rounded-3xl bg-[#0C0D0E] p-7 shadow-sm hover:shadow-xl hover:shadow-slate-300 hover:-translate-y-1 transition-all duration-200 h-full">
                <div className="flex justify-between items-start">
                  <span className="w-12 h-12 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] flex items-center justify-center">
                    <Globe2 className="w-6 h-6" />
                  </span>
                  <span className="w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center group-hover:bg-[#FF6B35] transition-colors">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
                <h3 className="font-display font-bold text-xl mt-5 tracking-tight text-white">{t("dest.globalPromoTitle")}</h3>
                <span className="mt-2 inline-block rounded-full bg-white/10 text-white/80 px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em]">
                  {globalDest.countries} {t("dest.countries")}
                </span>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono-t text-xs text-white/40">{globalDest.plans} {t("cat.plans")}</span>
                  <span className="font-mono-t text-sm font-bold text-[#FF6B35]">{t("featured.from")} {fmt(globalDest.min_price)}</span>
                </div>
              </Link>
            </Reveal>
          )}
          {regional && (
            <Reveal key="regional">
              <Link key={regional.code} to={`/destination/${regional.code}`} data-testid="destination-card-regional"
                className="group block rounded-3xl bg-white border-2 border-[#0D9488]/30 p-7 shadow-sm hover:shadow-xl hover:shadow-slate-300 hover:-translate-y-1 transition-all duration-200 h-full animate-fade-in">
                <div className="flex justify-between items-start">
                  <span className="w-12 h-12 rounded-full bg-[#E6F7F5] text-[#0D9488] flex items-center justify-center">
                    <MapPin className="w-6 h-6" />
                  </span>
                  <span className="w-9 h-9 rounded-full bg-[#F1F5F9] text-slate-600 flex items-center justify-center group-hover:bg-[#0D9488] group-hover:text-white transition-colors">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
                <div className="font-mono-t text-[9px] font-bold uppercase tracking-[0.25em] text-[#0D9488] mt-5">{t("home.regionalKicker")}</div>
                <h3 className="font-display font-bold text-xl mt-1 tracking-tight">{regional.name || regionName(regional.code)}</h3>
                <span className="mt-2 inline-block rounded-full bg-[#E6F7F5] text-[#0D9488] px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em]">
                  {regional.countries} {t("dest.countries")}
                </span>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono-t text-xs text-slate-400">{regional.plans} {t("cat.plans")}</span>
                  <span className="font-mono-t text-sm font-bold text-[#0D9488]">{t("featured.from")} {fmt(regional.min_price)}</span>
                </div>
              </Link>
            </Reveal>
          )}
          {featured.map((d, i) => (
            <Reveal key={d.code} delay={i * 0.08}>
              <Link to={`/destination/${d.code}`} data-testid={`destination-card-${d.code}`}
                className="group block rounded-3xl bg-white border border-slate-200/80 p-7 shadow-sm hover:shadow-xl hover:shadow-orange-100 hover:-translate-y-1 hover:border-[#FF6B35]/40 transition-all duration-200">
                <div className="flex justify-between items-start">
                  <Flag code={d.code} size="w-12 h-12" />
                  <span className="w-9 h-9 rounded-full bg-[#FFEDD5] text-[#FF6B35] flex items-center justify-center group-hover:bg-[#FF6B35] group-hover:text-white transition-colors">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
                <h3 className="font-display font-bold text-xl mt-5 tracking-tight text-slate-900">{regionName(d.code)}</h3>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono-t text-xs text-slate-400">{d.plans} {t("cat.plans")}</span>
                  <span className="font-mono-t text-sm font-bold text-[#FF6B35]">{t("featured.from")} {fmt(d.min_price)}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <Link to="/destinations" data-testid="featured-view-all"
            className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-8 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors shadow-lg shadow-orange-200">
            {t("featured.viewAll")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      {/* DAILY INSPIRATION */}
      {daily && (
        <section data-testid="home-daily" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-16 sm:pb-24">
          <Reveal>
            <div className="grid lg:grid-cols-[1.1fr_1fr] overflow-hidden rounded-[2.5rem] bg-[#1E293B] shadow-xl shadow-slate-200">
              <div className="relative min-h-[280px]">
                {daily.attraction.image ? (
                  <img data-testid="daily-image" src={imgSrc(daily.attraction.image)} alt={daily.attraction.name}
                    className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-[#FAF5EE]" />
                )}
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-5">{t("home.dailyEyebrow")}</div>
                <div className="flex items-center gap-3 mb-3">
                  <Flag code={daily.country_code} />
                  <span className="font-mono-t text-xs uppercase tracking-[0.2em] text-slate-400">{regionName(daily.country_code)}</span>
                </div>
                <h3 data-testid="daily-title" className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white">{daily.attraction.name}</h3>
                <p className="mt-4 text-slate-300 leading-relaxed">{daily.attraction.tip}</p>
                <Link data-testid="daily-cta" to={`/destination/${daily.country_code}`}
                  className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-[#FF6B35] text-white px-7 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors shadow-lg shadow-orange-900/40">
                  {t("home.dailyCta")} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* BLOG: ultimi 8 articoli */}
      <HomeBlogGrid />

      {/* MANIFESTO */}
      <section data-testid="manifesto-section" className="bg-[#F3ECE2]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-28">
          <Reveal>
            <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-14">{t("man.eyebrow")}</div>
          </Reveal>
          {[
            ["01", t("man.c1t"), t("man.c1x")],
            ["02", t("man.c2t"), t("man.c2x")],
            ["03", t("man.c3t"), t("man.c3x")],
          ].map(([n, title, text], i) => (
            <Reveal key={n} delay={i * 0.05}>
              <div data-testid={`manifesto-chapter-${n}`} className="grid md:grid-cols-[90px_1fr_1fr] gap-6 md:gap-12 py-11 border-t border-slate-900/10">
                <span className="font-mono-t text-sm font-bold text-[#FF6B35] pt-2">{n}</span>
                <h3 className="font-serif-e italic text-2xl sm:text-3xl leading-snug text-balance text-slate-900">{title}</h3>
                <p className="text-slate-600 leading-relaxed md:pt-2 max-w-md">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-24">
        <Reveal>
          <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("how.eyebrow")}</div>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 mb-14">{t("how.title")}</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            [<Globe2 key="1" className="w-6 h-6" />, t("how.s1t"), t("how.s1x")],
            [<ShieldCheck key="2" className="w-6 h-6" />, t("how.s2t"), t("how.s2x")],
            [<Zap key="3" className="w-6 h-6" />, t("how.s3t"), t("how.s3x")],
          ].map(([icon, title, text], i) => (
            <Reveal key={title} delay={i * 0.1}>
              <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-8 h-full hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-100 transition-all duration-200">
                <div className="flex items-center justify-between mb-8">
                  <span className="w-12 h-12 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center">{icon}</span>
                  <span className="font-mono-t text-xs font-bold text-slate-300">0{i + 1}</span>
                </div>
                <h3 className="font-display font-bold text-xl text-slate-900 mb-3">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BUSINESS USE CASES */}
      <section data-testid="home-business-cases" className="bg-[#1E293B]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-24">
          <Reveal>
            <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("home.bizEyebrow")}</div>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">{t("home.bizTitle")}</h2>
              <p className="max-w-sm text-slate-400">{t("home.bizSub")}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              [<Truck key="1" className="w-6 h-6" />, t("home.case1t"), t("home.case1d")],
              [<Users key="2" className="w-6 h-6" />, t("home.case2t"), t("home.case2d")],
            ].map(([icon, title, text], i) => (
              <Reveal key={title} delay={i * 0.1}>
                <div data-testid={`biz-case-${i + 1}`} className="rounded-3xl bg-white/5 border border-white/10 p-8 sm:p-10 h-full hover:bg-white/10 hover:-translate-y-1 transition-all duration-200">
                  <div className="flex items-center justify-between mb-8">
                    <span className="w-12 h-12 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] flex items-center justify-center">{icon}</span>
                    <span className="font-mono-t text-xs font-bold text-slate-600">0{i + 1}</span>
                  </div>
                  <h3 className="font-display font-bold text-xl sm:text-2xl text-white mb-3">{title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link to="/business" data-testid="home-business-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-8 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors shadow-lg shadow-orange-900/40">
              {t("home.bizCta")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* APP BANNER */}
      <section data-testid="home-app-banner" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-20">
        <Reveal>
          <div className="rounded-3xl bg-[#1E293B] p-8 sm:p-12 flex flex-col lg:flex-row items-center gap-10 overflow-hidden relative">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#FF6B35]/10 blur-2xl" />
            <div className="flex-1 relative">
              <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("appPage.eyebrow")}</div>
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-white">{t("home.appT")}</h2>
              <p className="mt-3 text-slate-400 max-w-lg">{t("home.appD")}</p>
              <Link to="/app" data-testid="home-app-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-8 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FF6B35] hover:text-white transition-colors">
                <Smartphone className="w-4 h-4" /> {t("home.appCta")}
              </Link>
            </div>
            <div className="relative shrink-0 hidden sm:block">
              <div className="w-44 h-80 rounded-[2rem] border-4 border-slate-700 bg-slate-800 shadow-2xl flex flex-col items-center justify-center gap-3 rotate-3 hover:rotate-0 transition-transform duration-300">
                <Smartphone className="w-10 h-10 text-[#FF6B35]" />
                <div className="font-display font-extrabold text-white tracking-tight">SIMAMMA</div>
                <div className="font-mono-t text-[9px] font-bold uppercase tracking-[0.25em] text-slate-400">{t("appPage.storeSoon")}</div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section data-testid="cta-band" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-20 sm:pb-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-[#E6F7F5] px-6 sm:px-16 py-20 sm:py-24 text-center">
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
              <div className="absolute top-8 left-10 w-10 h-5 rounded-full bg-white/70 animate-float" />
              <div className="absolute bottom-10 right-12 w-8 h-8 rounded-full bg-white/70 animate-float-delayed" />
              <div className="absolute top-1/2 right-[8%] w-6 h-6 rounded-md bg-[#14B8A6]/20 rotate-45 animate-float" />
            </div>
            <h2 className="relative font-display font-extrabold text-3xl sm:text-5xl leading-tight text-balance text-slate-900">{t("cta.title")}</h2>
            <p className="relative mt-5 text-slate-600 max-w-lg mx-auto">{t("cta.sub")}</p>
            <Link to="/destinations" data-testid="cta-get-esim"
              className="relative inline-flex items-center gap-2 mt-10 rounded-full bg-[#FF6B35] text-white px-10 py-4 font-mono-t text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#E8551F] transition-colors shadow-lg shadow-orange-200">
              {t("cta.button")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
